// src/pages/ProfilePage.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  updatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../utils/firebase";
import { getUserServiceUsage } from "../utils/trackingService";
import { getAllAnalyses, getUserSupportTickets } from "../utils/transactions";
import servicesConfig from "../utils/servicesConfig";

export default function ProfilePage() {
  const { user, userData, logout } = useAuth();
  const navigate = useNavigate();

  // ── Name form ──
  const [nameForm, setNameForm] = useState({ firstName: "", lastName: "" });
  const [nameStatus, setNameStatus] = useState("idle");
  const [nameError, setNameError] = useState("");

  // ── Password form ──
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordStatus, setPasswordStatus] = useState("idle");
  const [passwordError, setPasswordError] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);

  // ── Stats ──
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    totalPayments: 0,
    totalSpent: "$0.00",
    totalTickets: 0,
    serviceUsage: [],
    recentAnalyses: [],
    loading: true,
  });

  const fullName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() || "User";
  const memberSince = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString("en-US", { year: "numeric", month: "long" })
    : "Recently";

  // ── Load name from userData ──
  useEffect(() => {
    if (userData) {
      setNameForm({
        firstName: userData.firstName || "",
        lastName:  userData.lastName  || "",
      });
    }
  }, [userData]);

  // ── Load stats from Firestore ──
  useEffect(() => {
    async function loadStats() {
      if (!user) return;
      try {
        const [serviceUsage, analyses, tickets] = await Promise.all([
          getUserServiceUsage(user.uid),
          getAllAnalyses(user.uid),
          getUserSupportTickets(user.uid).catch(() => []),
        ]);

        // Calculate total spent
        const totalSpentCents = serviceUsage.reduce((sum, s) => {
          const plan = servicesConfig.plans.find(p => p.id === s.planId);
          return sum + (plan?.priceInCents || 0);
        }, 0);

        // Group service usage by serviceCode
        const usageByService = {};
        serviceUsage.forEach(s => {
          if (!usageByService[s.serviceCode]) {
            usageByService[s.serviceCode] = {
              serviceCode: s.serviceCode,
              serviceName: s.serviceName,
              serviceIcon: s.serviceIcon,
              count: 0,
              lastUsed: null,
            };
          }
          usageByService[s.serviceCode].count++;
          if (!usageByService[s.serviceCode].lastUsed) {
            usageByService[s.serviceCode].lastUsed = s.createdAt;
          }
        });

        setStats({
          totalAnalyses:  analyses.length,
          totalPayments:  serviceUsage.length,
          totalSpent:     `$${(totalSpentCents / 100).toFixed(2)}`,
          totalTickets:   tickets.length,
          serviceUsage:   Object.values(usageByService),
          recentAnalyses: analyses.slice(0, 3),
          loading:        false,
        });
      } catch (err) {
        console.error("loadStats error:", err);
        setStats(prev => ({ ...prev, loading: false }));
      }
    }
    loadStats();
  }, [user]);

  // ── Update Name ──
  async function handleUpdateName(e) {
    e.preventDefault();
    setNameError("");
    if (!nameForm.firstName.trim()) { setNameError("First name is required."); return; }
    if (!nameForm.lastName.trim())  { setNameError("Last name is required."); return; }

    setNameStatus("saving");
    try {
      await updateProfile(auth.currentUser, {
        displayName: `${nameForm.firstName} ${nameForm.lastName}`,
      });
      await updateDoc(doc(db, "users", user.uid), {
        firstName: nameForm.firstName.trim(),
        lastName:  nameForm.lastName.trim(),
      });
      setNameStatus("success");
      setTimeout(() => setNameStatus("idle"), 3000);
    } catch (err) {
      console.error("Update name error:", err);
      setNameError("Failed to update name. Please try again.");
      setNameStatus("error");
    }
  }

  // ── Update Password ──
  async function handleUpdatePassword(e) {
    e.preventDefault();
    setPasswordError("");

    if (!passwordForm.currentPassword) { setPasswordError("Please enter your current password."); return; }
    if (!passwordForm.newPassword)      { setPasswordError("Please enter a new password."); return; }
    if (passwordForm.newPassword.length < 6) { setPasswordError("New password must be at least 6 characters."); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordError("New passwords do not match."); return; }
    if (passwordForm.currentPassword === passwordForm.newPassword) { setPasswordError("New password must be different from current password."); return; }

    setPasswordStatus("saving");
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwordForm.newPassword);
      setPasswordStatus("success");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setPasswordStatus("idle"), 3000);
    } catch (err) {
      console.error("Update password error:", err);
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect.");
      } else if (err.code === "auth/weak-password") {
        setPasswordError("New password is too weak. Use at least 6 characters.");
      } else {
        setPasswordError("Failed to update password. Please try again.");
      }
      setPasswordStatus("error");
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  function update(form, setForm, field) {
    return (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ── NAV ── */}
      <nav style={{ borderBottom: "1px solid #e2e8f0", padding: "0 5%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div style={{ width: 32, height: 32, background: "linear-gradient(135deg,#2563eb,#1d4ed8)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>R</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 18, color: "#1e293b" }}>
            ResumeScore<span style={{ color: "#2563eb" }}>AI</span>
          </span>
        </Link>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-outline"
            style={{ fontSize: 13, padding: "7px 16px" }}
            onClick={() => navigate("/dashboard")}
          >
            ← Dashboard
          </button>
          <button
            style={{ fontSize: 13, padding: "7px 16px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontWeight: 500 }}
            onClick={handleLogout}
          >
            Log Out
          </button>
        </div>
      </nav>

      <div style={{ flex: 1, maxWidth: 760, margin: "0 auto", padding: "36px 20px", width: "100%" }}>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.025em", marginBottom: 4, color: "#1e293b" }}>
            My Profile
          </h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>
            Manage your account details and security settings.
          </p>
        </div>

        {/* ── Account Overview ── */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
            {/* Avatar */}
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(37,99,235,0.25)" }}>
              {(userData?.firstName || "U").charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 20, color: "#1e293b" }}>{fullName}</div>
              <div style={{ fontSize: 14, color: "#64748b", marginTop: 2 }}>{user?.email}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 12, color: "#2563eb", fontWeight: 500 }}>
                  📅 Member since {memberSince}
                </span>
                {user?.emailVerified && (
                  <span style={{ fontSize: 11, background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                    ✅ Verified
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats grid */}
          {stats.loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#2563eb", animation: "spin 0.7s linear infinite" }} />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
              {[
                { icon: "📊", label: "Total Analyses", value: stats.totalAnalyses, color: "#2563eb", bg: "#eff6ff" },
                { icon: "💳", label: "Total Payments", value: stats.totalPayments, color: "#059669", bg: "#ecfdf5" },
                { icon: "💰", label: "Total Spent", value: stats.totalSpent, color: "#d97706", bg: "#fffbeb" },
                { icon: "🎧", label: "Support Tickets", value: stats.totalTickets, color: "#7c3aed", bg: "#f5f3ff" },
              ].map(s => (
                <div key={s.label} style={{ textAlign: "center", padding: "14px 10px", background: s.bg, borderRadius: 12 }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 22, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 3, fontWeight: 500 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Service Usage ── */}
        {!stats.loading && stats.serviceUsage.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Service Usage</h2>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>Services you have used so far.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {stats.serviceUsage.map(s => (
                <div key={s.serviceCode} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{s.serviceIcon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{s.serviceName}</div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                        {s.lastUsed?.seconds
                          ? `Last used: ${new Date(s.lastUsed.seconds * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`
                          : "Recently used"
                        }
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: "#2563eb" }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{s.count === 1 ? "time" : "times"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Recent Analyses ── */}
        {!stats.loading && stats.recentAnalyses.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 2, color: "#1e293b" }}>Recent Analyses</h2>
                <p style={{ fontSize: 13, color: "#64748b" }}>Your last 3 resume analyses.</p>
              </div>
              <button
                onClick={() => navigate("/dashboard")}
                style={{ fontSize: 13, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}
              >
                View All →
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {stats.recentAnalyses.map((a, i) => {
                const scoreColor = a.ats_score >= 75 ? "#059669" : a.ats_score >= 50 ? "#d97706" : "#dc2626";
                const scoreBg    = a.ats_score >= 75 ? "#ecfdf5" : a.ats_score >= 50 ? "#fffbeb" : "#fef2f2";
                const date = a.createdAt?.seconds
                  ? new Date(a.createdAt.seconds * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                  : "Unknown";
                return (
                  <div key={a.id || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ background: scoreBg, borderRadius: 10, padding: "8px 14px", textAlign: "center", minWidth: 60 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{a.ats_score}</div>
                        <div style={{ fontSize: 10, color: scoreColor, fontWeight: 500 }}>score</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>Analysis #{stats.recentAnalyses.length - i}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{date}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748b" }}>
                      <span>Match: <strong style={{ color: "#1e293b" }}>{a.match_rate}%</strong></span>
                      <span>Missing: <strong style={{ color: "#dc2626" }}>{a.missing_count}</strong></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Personal Information ── */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Personal Information</h2>
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Update your first and last name.</p>

          {nameStatus === "success" && (
            <div style={{ background: "#dcfce7", color: "#166534", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16, border: "1px solid #bbf7d0" }}>
              ✅ Name updated successfully!
            </div>
          )}
          {nameError && (
            <div style={{ background: "#fef2f2", color: "#dc2626", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16, border: "1px solid #fecaca" }}>
              {nameError}
            </div>
          )}

          <form onSubmit={handleUpdateName}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>First name</label>
                <input
                  className="form-input"
                  type="text"
                  value={nameForm.firstName}
                  onChange={update(nameForm, setNameForm, "firstName")}
                  placeholder="Jane"
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Last name</label>
                <input
                  className="form-input"
                  type="text"
                  value={nameForm.lastName}
                  onChange={update(nameForm, setNameForm, "lastName")}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>
                Email address
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>(cannot be changed)</span>
              </label>
              <input
                className="form-input"
                type="email"
                value={user?.email || ""}
                disabled
                style={{ background: "#f8fafc", color: "#94a3b8", cursor: "not-allowed" }}
              />
            </div>

            <button
              type="submit"
              style={{ padding: "10px 24px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: nameStatus === "saving" ? "not-allowed" : "pointer", opacity: nameStatus === "saving" ? 0.7 : 1 }}
              disabled={nameStatus === "saving"}
            >
              {nameStatus === "saving"
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving...</>
                : "Save Changes"
              }
            </button>
          </form>
        </div>

        {/* ── Change Password ── */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Change Password</h2>
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Choose a strong password with at least 6 characters.</p>

          {passwordStatus === "success" && (
            <div style={{ background: "#dcfce7", color: "#166534", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16, border: "1px solid #bbf7d0" }}>
              ✅ Password updated successfully!
            </div>
          )}
          {passwordError && (
            <div style={{ background: "#fef2f2", color: "#dc2626", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16, border: "1px solid #fecaca" }}>
              {passwordError}
            </div>
          )}

          <form onSubmit={handleUpdatePassword}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Current password</label>
              <input
                className="form-input"
                type={showPasswords ? "text" : "password"}
                value={passwordForm.currentPassword}
                onChange={update(passwordForm, setPasswordForm, "currentPassword")}
                placeholder="Enter your current password"
                required
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>New password</label>
              <input
                className="form-input"
                type={showPasswords ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={update(passwordForm, setPasswordForm, "newPassword")}
                placeholder="At least 6 characters"
                required
              />
              {passwordForm.newPassword && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ height: 4, background: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 2, transition: "width 0.3s",
                      width:      passwordForm.newPassword.length < 6 ? "25%" : passwordForm.newPassword.length < 10 ? "60%" : "100%",
                      background: passwordForm.newPassword.length < 6 ? "#dc2626" : passwordForm.newPassword.length < 10 ? "#d97706" : "#059669",
                    }} />
                  </div>
                  <div style={{ fontSize: 11, marginTop: 3, color: passwordForm.newPassword.length < 6 ? "#dc2626" : passwordForm.newPassword.length < 10 ? "#d97706" : "#059669" }}>
                    {passwordForm.newPassword.length < 6 ? "Weak" : passwordForm.newPassword.length < 10 ? "Fair" : "Strong"}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Confirm new password</label>
              <input
                className="form-input"
                type={showPasswords ? "text" : "password"}
                value={passwordForm.confirmPassword}
                onChange={update(passwordForm, setPasswordForm, "confirmPassword")}
                placeholder="Repeat your new password"
                required
              />
              {passwordForm.confirmPassword && (
                <div style={{ fontSize: 11, marginTop: 4, color: passwordForm.newPassword === passwordForm.confirmPassword ? "#059669" : "#dc2626" }}>
                  {passwordForm.newPassword === passwordForm.confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <input type="checkbox" id="showPwd" checked={showPasswords} onChange={e => setShowPasswords(e.target.checked)} style={{ cursor: "pointer" }} />
              <label htmlFor="showPwd" style={{ fontSize: 13, color: "#374151", cursor: "pointer" }}>Show passwords</label>
            </div>

            <button
              type="submit"
              style={{ padding: "10px 24px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: passwordStatus === "saving" ? "not-allowed" : "pointer", opacity: passwordStatus === "saving" ? 0.7 : 1 }}
              disabled={passwordStatus === "saving"}
            >
              {passwordStatus === "saving"
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Updating...</>
                : "Update Password"
              }
            </button>
          </form>
        </div>

        {/* ── Quick Links ── */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#1e293b" }}>Quick Links</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { icon: "📊", label: "My Analyses",      desc: "View all reports",           action: () => navigate("/dashboard") },
              { icon: "✨", label: "Resume Generator", desc: "Generate ATS resume free",   action: () => navigate("/generate-resume") },
              { icon: "🎧", label: "Support",          desc: "Get help with any issue",    action: () => navigate("/support") },
              { icon: "🏠", label: "Dashboard",        desc: "Back to main page",          action: () => navigate("/dashboard") },
            ].map(item => (
              <button
                key={item.label}
                onClick={item.action}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, cursor: "pointer", textAlign: "left" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.borderColor = "#bfdbfe"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{item.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Danger Zone ── */}
        <div style={{ border: "1px solid #fecaca", borderRadius: 14, padding: "20px 24px", background: "#fff" }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>⚠️ Account Actions</h2>
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
            Need to log out? Click the button below.
          </p>
          <button
            onClick={handleLogout}
            style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#dc2626"; }}
          >
            🚪 Log Out
          </button>
        </div>

      </div>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid #e2e8f0", padding: "16px 5%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
          ResumeScore<span style={{ color: "#2563eb" }}>AI</span>
        </span>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>
          © {new Date().getFullYear()} ResumeScoreAI. All rights reserved.
        </span>
      </footer>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}